import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { Typography, Paper, Box, Chip, TextField, Stack, Tooltip, useTheme, Skeleton, IconButton } from '@mui/material';
import { IoSparklesOutline } from 'react-icons/io5';
import { MdCheck, MdClose, MdOutlineModeEdit } from 'react-icons/md';
import Button from '@/components/Common/Button';
import { useGenerateSentimentKeywords, useGetDefaultSentimentKeywords } from '@/apis/business/agent/signals/hook';
import {
  useGeneratePublicSentimentKeywords,
  useGetPublicDefaultSentimentKeywords
} from '@/apis/public/onboardingForms/hooks';
import { titleCase } from '@/utils/helperFunctions';
import { SignalKeywordItem } from '@/apis/business/agent/signals/types';

type EditingSource = 'suggested' | 'selected';

interface EditingContext {
  source: EditingSource;
  originalKeyword: string;
}

const CustomerSentimentsForm = () => {
  const theme = useTheme();
  const location = useLocation();
  const isPublicRoute = location.pathname.includes('/business-onboarding/stage-2');

  const { watch, setValue } = useFormContext();

  const [trackingNotes, setTrackingNotes] = useState('');
  const selectedKeywords: SignalKeywordItem[] = watch('custom_keywords') ?? [];

  const [suggestedTags, setSuggestedTags] = useState<SignalKeywordItem[]>([]);
  const [editingContext, setEditingContext] = useState<EditingContext | null>(null);
  const [draftKeyword, setDraftKeyword] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameBeforeEdit, setNameBeforeEdit] = useState('');
  const [duplicateError, setDuplicateError] = useState('');

  const businessDefaultQuery = useGetDefaultSentimentKeywords(!isPublicRoute);
  const publicDefaultQuery = useGetPublicDefaultSentimentKeywords(isPublicRoute);
  const businessGenerate = useGenerateSentimentKeywords();
  const publicGenerate = useGeneratePublicSentimentKeywords();

  const { data: systemKeywordsResult, isLoading: isSystemKeywordsLoading } = isPublicRoute
    ? publicDefaultQuery
    : businessDefaultQuery;

  const { mutate: generateKeywordsMutation, isPending: isGeneratePending } = isPublicRoute
    ? publicGenerate
    : businessGenerate;

  const systemKeywords = systemKeywordsResult?.data ?? [];

  const updateSelected = (next: SignalKeywordItem[]) => {
    setValue('custom_keywords', next, { shouldDirty: true });
  };

  const closeEditor = () => {
    setEditingContext(null);
    setDraftKeyword('');
    setDraftDescription('');
    setIsEditingName(false);
    setNameBeforeEdit('');
    setDuplicateError('');
  };

  const openEditor = (item: SignalKeywordItem, source: EditingSource) => {
    setEditingContext({ source, originalKeyword: item.keyword });
    setDraftKeyword(item.keyword);
    setDraftDescription(item.description ?? '');
    setIsEditingName(false);
    setNameBeforeEdit('');
    setDuplicateError('');
  };

  const isDuplicateKeyword = (keyword: string, excludeOriginal?: string) => {
    const normalized = keyword.trim().toLowerCase();
    return selectedKeywords.some((k) => {
      if (excludeOriginal && k.keyword.toLowerCase() === excludeOriginal.toLowerCase()) {
        return false;
      }
      return k.keyword.toLowerCase() === normalized;
    });
  };

  const handleGenerate = () => {
    const prompt = trackingNotes.trim();
    if (!prompt) return;

    generateKeywordsMutation(prompt, {
      onSuccess: (res) => {
        closeEditor();
        const rows = res?.data ?? [];
        const existingKeys = new Set(selectedKeywords.map((k) => k.keyword.toLowerCase()));
        setSuggestedTags(rows.filter((row) => !existingKeys.has(row.keyword.toLowerCase())));
      }
    });
  };

  const handleConfirmKeyword = () => {
    const keyword = draftKeyword.trim();
    const description = draftDescription.trim();

    if (!keyword) return;

    if (!editingContext) return;

    const excludeOriginal = editingContext.source === 'selected' ? editingContext.originalKeyword : undefined;
    if (isDuplicateKeyword(keyword, excludeOriginal)) {
      setDuplicateError('A keyword with this name already exists.');
      return;
    }

    const updatedItem: SignalKeywordItem = { keyword, description };

    if (editingContext.source === 'suggested') {
      updateSelected([...selectedKeywords, updatedItem]);
      setSuggestedTags((prev) => prev.filter((k) => k.keyword !== editingContext.originalKeyword));
    } else {
      updateSelected(selectedKeywords.map((k) => (k.keyword === editingContext.originalKeyword ? updatedItem : k)));
    }

    closeEditor();
  };

  const handleRemoveSelected = (keyword: string) => {
    if (editingContext?.source === 'selected' && editingContext.originalKeyword === keyword) {
      closeEditor();
    }
    updateSelected(selectedKeywords.filter((k) => k.keyword !== keyword));
  };

  const handleStartEditName = () => {
    setNameBeforeEdit(draftKeyword);
    setIsEditingName(true);
  };

  const handleConfirmEditName = () => {
    setDraftKeyword(draftKeyword.trim());
    setIsEditingName(false);
    setDuplicateError('');
  };

  const handleCancelEditName = () => {
    setDraftKeyword(nameBeforeEdit);
    setIsEditingName(false);
  };

  const isConfirmDisabled = !draftKeyword.trim();

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid #EAEAEA',
        borderRadius: 2,
        p: { xs: 2.5, md: 4 }
      }}
    >
      <Typography fontWeight={500} fontSize={20} mb={0.5}>
        Customer Sentiments
      </Typography>

      <Typography variant='body2' color='text.secondary' fontSize={16} mb={3}>
        What customers say about your business
      </Typography>

      <Box mb={4}>
        <Stack direction='row' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap={1} mb={1.5}>
          <Stack direction='row' alignItems='center' gap={1}>
            <Typography fontWeight={500} fontSize={16}>
              Default Keywords
            </Typography>
            <Chip
              label='System'
              size='small'
              sx={{
                bgcolor: theme.palette.background.default,
                color: theme.palette.common.black,
                border: `1px solid ${theme.palette.info.main}`,
                borderRadius: '48px',
                padding: '12px'
              }}
            />
          </Stack>
          <Typography variant='caption' color='text.secondary'>
            Auto-tracked — Read only
          </Typography>
        </Stack>

        <Box
          sx={{
            border: `1px solid ${theme.palette.info.main}`,
            borderRadius: 2,
            p: 2,
            minHeight: 56
          }}
        >
          <Stack direction='row' flexWrap='wrap' gap={1}>
            {isSystemKeywordsLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} variant='rounded' width={110} height={30} sx={{ borderRadius: '48px' }} />
              ))
            ) : systemKeywords.length === 0 ? (
              <Typography variant='body2' color='text.secondary'>
                No default keywords available yet.
              </Typography>
            ) : (
              systemKeywords.map((item) => (
                <Tooltip key={item.keyword} title={item.description || ''} placement='top' arrow>
                  <Chip
                    label={titleCase(item.keyword)}
                    size='small'
                    sx={{
                      bgcolor: theme.palette.background.default,
                      color: theme.palette.common.black,
                      border: `1px solid ${theme.palette.info.main}`,
                      borderRadius: '48px',
                      padding: '12px'
                    }}
                  />
                </Tooltip>
              ))
            )}
          </Stack>
        </Box>
      </Box>

      <Box>
        <Stack direction='row' alignItems='center' gap={1} mb={1}>
          <Typography fontWeight={500} fontSize={16}>
            Business-Specific Keywords
          </Typography>
          <Chip
            label='Custom'
            size='small'
            sx={{ bgcolor: theme.palette.primary.light, color: theme.palette.primary.main, borderRadius: '48px' }}
          />
        </Stack>

        <Typography variant='body2' color='text.secondary' mb={2} fontSize={16}>
          Tell us what you want to track in simple words, we&apos;ll create keywords for it.
        </Typography>

        <TextField
          fullWidth
          multiline
          minRows={5}
          maxRows={12}
          value={trackingNotes}
          onChange={(e) => setTrackingNotes(e.target.value)}
          placeholder='Describe what you want to track on calls…'
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: '#fff'
            }
          }}
        />

        <Box display='flex' justifyContent='flex-end' mt={2}>
          <Button
            variant='contained'
            sx={{
              bgcolor: theme.palette.primary.main,
              '&:hover': {
                bgcolor: theme.palette.primary.dark
              }
            }}
            startIcon={<IoSparklesOutline size={18} />}
            onClick={handleGenerate}
            isPending={isGeneratePending}
            disabled={!trackingNotes.trim()}
          >
            Generate
          </Button>
        </Box>

        {suggestedTags?.length > 0 && (
          <Box mt={3}>
            <Typography variant='body2' color='text.secondary' mb={1.5} fontSize={16}>
              Select Tags to Add:
            </Typography>

            <Stack direction='row' flexWrap='wrap' gap={1}>
              {suggestedTags?.map((item) => {
                const isActive =
                  editingContext?.source === 'suggested' && editingContext.originalKeyword === item.keyword;

                return (
                  <Tooltip key={item.keyword} title={item.description || ''} placement='top' arrow>
                    <Chip
                      label={`+ ${titleCase(item.keyword)}`}
                      clickable
                      onClick={() => openEditor(item, 'suggested')}
                      sx={{
                        bgcolor: isActive ? theme.palette.primary.light : theme.palette.background.paper,
                        border: `1px solid ${isActive ? theme.palette.primary.main : theme.palette.divider}`,
                        color: isActive ? theme.palette.primary.main : theme.palette.text.primary,
                        borderRadius: '999px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: isActive ? theme.palette.primary.light : theme.palette.action.hover
                        }
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Stack>
          </Box>
        )}

        {editingContext && (
          <Box
            mt={2}
            p={2.5}
            sx={{
              bgcolor: theme.palette.action.hover,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2
            }}
          >
            <Stack direction='row' alignItems='center' gap={0.5} mb={2}>
              {isEditingName ? (
                <>
                  <TextField
                    size='small'
                    value={draftKeyword}
                    onChange={(e) => {
                      setDraftKeyword(e.target.value);
                      setDuplicateError('');
                    }}
                    autoFocus
                    sx={{
                      minWidth: 180,
                      '& .MuiOutlinedInput-root': {
                        bgcolor: '#fff'
                      }
                    }}
                  />
                  <IconButton size='small' onClick={handleConfirmEditName} color='primary'>
                    <MdCheck size={18} />
                  </IconButton>
                  <IconButton size='small' onClick={handleCancelEditName}>
                    <MdClose size={18} />
                  </IconButton>
                </>
              ) : (
                <>
                  <Typography fontWeight={500} fontSize={16}>
                    {titleCase(draftKeyword)}
                  </Typography>
                  <IconButton size='small' onClick={handleStartEditName} sx={{ color: theme.palette.primary.main }}>
                    <MdOutlineModeEdit size={16} />
                  </IconButton>
                </>
              )}
            </Stack>

            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={8}
              value={draftDescription}
              onChange={(e) => setDraftDescription(e.target.value)}
              placeholder='Describe what this keyword tracks…'
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#fff'
                }
              }}
            />

            {duplicateError && (
              <Typography variant='body2' color='error' mb={1.5}>
                {duplicateError}
              </Typography>
            )}

            <Stack direction='row' justifyContent='flex-end' gap={1}>
              <Button variant='outlined' onClick={closeEditor}>
                Cancel
              </Button>
              <Button
                variant='contained'
                onClick={handleConfirmKeyword}
                disabled={isConfirmDisabled}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  '&:hover': {
                    bgcolor: theme.palette.primary.dark
                  }
                }}
              >
                {editingContext.source === 'suggested' ? 'Add Keyword' : 'Save'}
              </Button>
            </Stack>
          </Box>
        )}

        {selectedKeywords.length > 0 && (
          <Stack direction='row' flexWrap='wrap' gap={1} mt={2}>
            {selectedKeywords.map((item) => {
              const isActive = editingContext?.source === 'selected' && editingContext.originalKeyword === item.keyword;

              return (
                <Tooltip key={item.keyword} placement='top' title={item.description || ''} arrow>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.25,
                      pl: 1.5,
                      pr: 0.5,
                      py: 0.25,
                      border: `1px solid ${theme.palette.primary.main}`,
                      borderRadius: '48px',
                      bgcolor: isActive ? theme.palette.primary.main : theme.palette.primary.light,
                      color: isActive ? theme.palette.primary.contrastText : theme.palette.primary.main
                    }}
                  >
                    <Typography variant='body2' fontWeight={500} fontSize={13}>
                      {titleCase(item.keyword)}
                    </Typography>
                    <IconButton
                      size='small'
                      onClick={() => openEditor(item, 'selected')}
                      sx={{
                        color: isActive ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        p: 0.25
                      }}
                    >
                      <MdOutlineModeEdit size={14} />
                    </IconButton>
                    <IconButton
                      size='small'
                      onClick={() => handleRemoveSelected(item.keyword)}
                      sx={{
                        color: isActive ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        p: 0.25
                      }}
                    >
                      <MdClose size={14} />
                    </IconButton>
                  </Box>
                </Tooltip>
              );
            })}
          </Stack>
        )}
      </Box>
    </Paper>
  );
};

export default CustomerSentimentsForm;
